<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import validators from "@/utils/validators";
import { useSnackbarStore } from "@/stores/snackbarStore";
import MandateService from "@/services/mandate/mandateService";

import type { ValidationRule } from "@/types/utils/validators.type";
import { PeriodicityKey } from "@/enums/periodicity_enum";
import type { AdminExpenseManagement } from '@/types/services/mandate/mandate.type.ts';

const props = defineProps<{
  mandateId: number;
  drawerMode?: boolean;
}>();

const emit = defineEmits<{
  (e: "updated"): void;
}>();

const { t } = useI18n();
const snackbarStore = useSnackbarStore();

const loading = ref(false);
const formValid = ref(false);

const percentageRules = ref<ValidationRule[]>([...validators.mandatePercentageRules]);

const form = reactive({
  managementFee: 0.0,
  managementPeriodicity: PeriodicityKey.MONTHLY as PeriodicityKey,
  accountKeepingFee: 0.0,
});

// Values as loaded, to send only what the admin actually changed.
const initial = reactive({
  managementFee: 0.0,
  managementPeriodicity: PeriodicityKey.MONTHLY as PeriodicityKey,
  accountKeepingFee: 0.0,
});

// Sending the platform fees switches them to a derogation: only send them when they changed.
const platformFeesChanged = computed(() =>
  Number(form.managementFee) !== initial.managementFee ||
  form.managementPeriodicity !== initial.managementPeriodicity
);
const accountKeepingChanged = computed(() => Number(form.accountKeepingFee) !== initial.accountKeepingFee);
const hasChanges = computed(() => platformFeesChanged.value || accountKeepingChanged.value);

const periodicityOptions = computed(() => [
  { title: t("common.periodicity.monthly"), value: PeriodicityKey.MONTHLY },
  { title: t("common.periodicity.quarterly"), value: PeriodicityKey.QUARTERLY },
  { title: t("common.periodicity.semiannual"), value: PeriodicityKey.SEMIANNUAL },
  { title: t("common.periodicity.yearly"), value: PeriodicityKey.YEARLY },
]);

watch(
  () => props.mandateId,
  (mandateId) => {
    if (!mandateId) return;
    load(mandateId);
  },
  { immediate: true }
);

async function load(mandateId: number): Promise<void> {
  loading.value = true;
  try {
    const response: AdminExpenseManagement = await MandateService.getAdminExpenseManagement(mandateId);

    form.managementFee = Number(response.managementFee);
    form.managementPeriodicity = response.managementPeriodicity;
    form.accountKeepingFee = Number(response.accountKeepingFee ?? 0);

    initial.managementFee = form.managementFee;
    initial.managementPeriodicity = form.managementPeriodicity;
    initial.accountKeepingFee = form.accountKeepingFee;
  } finally {
    loading.value = false;
  }
}

async function submit(): Promise<void> {
  if (!formValid.value || !hasChanges.value) return;

  loading.value = true;
  try {
    await MandateService.updateAdminExpenseManagement(props.mandateId, {
      ...(platformFeesChanged.value && {
        managementFee: Number(form.managementFee),
        managementPeriodicity: form.managementPeriodicity,
      }),
      ...(accountKeepingChanged.value && { accountKeepingFee: Number(form.accountKeepingFee) }),
    });

    snackbarStore.openSnackbar({
      text: t("views.editMandateFees.updateSuccess"),
      color: "success",
    });

    emit("updated");
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <v-row>
    <v-col cols="12">
      <v-form v-model="formValid" @submit.prevent="submit">
        <v-row>
          <v-col cols="12" class="d-flex align-center">
            <v-icon class="mr-2" color="primary" icon="PhCoin" />
            <h2 class="subtitle">{{ t("views.editMandateFees.subtitle") }}</h2>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.managementFee"
              :label="t('views.editMandateFees.inputs.managementFee.label')"
              :placeholder="t('views.editMandateFees.inputs.managementFee.placeholder')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              v-model="form.managementPeriodicity"
              :label="t('views.editMandateFees.inputs.managementPeriodicity.label')"
              :items="periodicityOptions"
              item-title="title"
              item-value="value"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.accountKeepingFee"
              :label="t('views.editMandateFees.inputs.accountKeepingFee.label')"
              :placeholder="t('views.editMandateFees.inputs.accountKeepingFee.placeholder')"
              :hint="t('views.editMandateFees.inputs.accountKeepingFee.hint')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              persistent-hint
              suffix="%"
              type="number"
            />
          </v-col>

          <v-col cols="12" class="d-flex justify-end">
            <v-btn
              :disabled="!formValid || !hasChanges || loading"
              :loading="loading"
              :text="t('common.buttons.update')"
              class="w-100 w-sm-auto"
              type="submit"
            >
              <template #prepend>
                <v-icon icon="PhCheck" />
              </template>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>
